// T-051-W — one shared helper for both web call sites that join a
// firstName/lastName pair, so the fix cannot be skipped by a second call
// site the way mobile's `_cleanName` was (see T-051's own postmortem).
//
// 🔒 T-057-W — the backend writes a literal PLACEHOLDER CHARACTER when an
// inbound name has no last (or first) part (`nameParts.slice(1).join(' ')
// || '-'` at most write sites, but an EM DASH U+2014 at sales.routes.ts:2998
// — see T-051's own postmortem on trusting a single character). A part is
// ABSENT when its trimmed value contains NO LETTER AND NO DIGIT — a CLASS,
// not an enumerated list of separator characters (a longer list is the same
// defect one level up: `.`, `--` and `·` are all outside any dash-codepoint
// list). "Letter" is Unicode-aware (`\p{L}`/`\p{N}`, `u` flag) — an ASCII
// `[a-zA-Z]` check would wrongly blank real names like `राज`. This matches
// mobile's `_blankIfNoLetterOrDigit` (renamed from `_blankIfDashOnly` in
// `6ca86435` — the old name had become a lie about what it does; T-057-M)
// so the two surfaces cannot disagree on what "no name" means. A part that
// merely CONTAINS a separator is still a name — `Jean-Paul` stays
// `Jean-Paul`.

const HAS_NAME_CHARACTER = /[\p{L}\p{N}]/u;

function cleanNamePart(part: string | null | undefined): string {
  const trimmed = (part ?? '').trim();
  return HAS_NAME_CHARACTER.test(trimmed) ? trimmed : '';
}

/**
 * "First Last" from parts that may carry the backend's '-' placeholder for
 * an absent part. Returns `fallback` (default `''`) when both parts are
 * absent, so callers keep whatever their own next fallback step already is.
 */
export function joinPersonName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback = '',
): string {
  const parts = [cleanNamePart(firstName), cleanNamePart(lastName)].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : fallback;
}

/**
 * Avatar-style initials from the same two parts — a DIFFERENT shape from
 * `joinPersonName`, not covered by it: `'-'.charAt(0)` is still `'-'`, so an
 * avatar built from raw parts reads e.g. "S-" for a contact with a stored
 * '-' last name. Same absent-check, applied BEFORE the initial is taken.
 */
export function initialsFor(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback = 'U',
): string {
  const f = cleanNamePart(firstName);
  const l = cleanNamePart(lastName);
  const initials = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return initials || fallback;
}
